import { ShieldCheck, ShieldAlert, ShieldOff, UploadCloud, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { Certificado, uploadCertificado } from '../api/certificado';
import { apiError } from '../api/client';
import { formatDate } from '../utils/formatters';

type Tone = 'ok' | 'aviso' | 'critico' | 'vencido' | 'nenhum';

function tone(cert: Certificado | null | undefined): Tone {
  if (!cert) return 'nenhum';
  if (cert.situacao === 'vencido') return 'vencido';
  if (cert.situacao === 'critico') return 'critico';
  if (cert.situacao === 'aviso') return 'aviso';
  return 'ok';
}

const LOOK: Record<Tone, {
  label: string;
  Icon: typeof ShieldCheck;
  border: string;
  bg: string;
  text: string;
  dot: string;
}> = {
  ok: {
    label: 'Válido',
    Icon: ShieldCheck,
    border: 'border-emerald-200 dark:border-emerald-500/30',
    bg: 'bg-emerald-50 dark:bg-emerald-500/5',
    text: 'text-emerald-700 dark:text-emerald-300',
    dot: 'bg-emerald-400'
  },
  aviso: {
    label: 'Próximo do vencimento',
    Icon: ShieldAlert,
    border: 'border-amber-200 dark:border-amber-500/40',
    bg: 'bg-amber-50 dark:bg-amber-500/5',
    text: 'text-amber-700 dark:text-amber-300',
    dot: 'bg-amber-400'
  },
  critico: {
    label: 'Vence em dias',
    Icon: ShieldAlert,
    border: 'border-orange-200 dark:border-orange-500/40',
    bg: 'bg-orange-50 dark:bg-orange-500/5',
    text: 'text-orange-700 dark:text-orange-300',
    dot: 'bg-orange-400'
  },
  vencido: {
    label: 'Vencido',
    Icon: ShieldOff,
    border: 'border-rose-200 dark:border-rose-500/40',
    bg: 'bg-rose-50 dark:bg-rose-500/5',
    text: 'text-rose-700 dark:text-rose-300',
    dot: 'bg-rose-400'
  },
  nenhum: {
    label: 'Sem certificado',
    Icon: ShieldOff,
    border: 'border-slate-200 dark:border-neutral-500/30',
    bg: 'bg-slate-50 dark:bg-neutral-500/5',
    text: 'text-slate-600 dark:text-neutral-300',
    dot: 'bg-neutral-400'
  }
};

interface Props {
  emitenteId: number;
  cert: Certificado | null | undefined;
  onUploaded?: () => void;
}

export default function CertificadoStatusCard({ emitenteId, cert, onUploaded }: Props) {
  const t = tone(cert);
  const look = LOOK[t];
  const dias = cert?.dias_restantes ?? null;

  // abre automaticamente apenas se não tem certificado; vencido fica discreto (1 linha)
  const [expandido, setExpandido] = useState(t === 'nenhum');
  const [file, setFile] = useState<File | null>(null);
  const [senha, setSenha] = useState('');
  const [busy, setBusy] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  async function enviar() {
    if (!file || !senha) {
      setErro('Escolha o arquivo .pfx ou .p12 e informe a senha.');
      return;
    }
    setBusy(true);
    setErro(null);
    setOk(null);
    try {
      await uploadCertificado(emitenteId, file, senha);
      setOk('Certificado enviado com sucesso.');
      setFile(null);
      setSenha('');
      onUploaded?.();
      setTimeout(() => setExpandido(false), 1500);
    } catch (e) {
      setErro(apiError(e, 'Erro ao enviar certificado'));
    } finally {
      setBusy(false);
    }
  }

  const resumo = cert?.data_validade
    ? `Válido até ${formatDate(cert.data_validade)}${dias != null ? ` · ${Math.max(0, dias)} dias` : ''}`
    : 'Envie um arquivo .pfx / .p12 para habilitar a emissão.';

  return (
    <section className={`card-surface border ${look.border} ${look.bg}`}>
      <button
        type="button"
        onClick={() => setExpandido((v) => !v)}
        className="w-full flex items-center gap-3 p-3 md:p-3.5 text-left hover:bg-slate-100/70 transition rounded-2xl dark:hover:bg-white/[0.02]"
        aria-expanded={expandido}
      >
        <div className={`shrink-0 h-9 w-9 rounded-lg grid place-items-center border ${look.border} ${look.bg}`}>
          <look.Icon className={`h-4 w-4 ${look.text}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="eyebrow">Certificado A1</span>
            <span className={`h-1.5 w-1.5 rounded-full ${look.dot}`} />
            <span className={`text-xs font-semibold ${look.text}`}>{look.label}</span>
          </div>
          <div className="text-xs text-neutral-600 truncate mt-0.5 dark:text-neutral-400">{resumo}</div>
        </div>
        {dias != null && (
          <div className="shrink-0 text-right pr-1">
            <div className={`text-xl font-bold tabular-nums leading-none ${look.text}`}>{Math.max(0, dias)}</div>
            <div className="text-[9px] uppercase tracking-wider text-neutral-500 mt-0.5">dias</div>
          </div>
        )}
        <ChevronDown className={`h-4 w-4 text-neutral-500 shrink-0 transition-transform ${expandido ? 'rotate-180' : ''}`} />
      </button>

      {expandido && (
        <div className="px-3 md:px-3.5 pb-3 md:pb-3.5 pt-1 border-t border-slate-200 space-y-3 dark:border-white/5">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 md:gap-3 items-end">
            <div>
              <label className="field-label">Arquivo (.pfx / .p12)</label>
              <input
                type="file"
                accept=".pfx,.p12"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block w-full text-xs text-neutral-600 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 file:cursor-pointer dark:text-neutral-300 dark:file:bg-white/10 dark:file:text-white dark:hover:file:bg-white/20"
              />
            </div>
            <div>
              <label className="field-label">Senha</label>
              <input
                type="password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                placeholder="Senha do .pfx"
                className="field-input !py-2"
              />
            </div>
            <button
              type="button"
              onClick={enviar}
              disabled={busy || !file || !senha}
              className="btn-primary !py-2 text-sm"
            >
              <UploadCloud className="h-4 w-4" />
              {busy ? 'Enviando...' : 'Enviar'}
            </button>
          </div>
          {erro && <div className="text-xs font-medium text-rose-700 dark:text-rose-400">{erro}</div>}
          {ok && <div className="text-xs font-medium text-emerald-700 dark:text-emerald-400">{ok}</div>}
        </div>
      )}
    </section>
  );
}
